import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons'
import { faGripVertical } from '@fortawesome/pro-light-svg-icons'
import { Box } from '@pinchenterprisesnpm/friday-ui'
import { selectBusinessEntity } from '@src/data/BusinessEntity/selectors'
import { InfoBanner } from '@src/features/shift-post-v2/components/info-banner'
import { ReorderRosters } from '@src/features/shift-post-v2/state/shift-post-v2.actions'
import { selectRosters } from '@src/features/shift-post-v2/state/shift-post-v2.selectors'
import { ACCEPTANCE_CRITERIA_OPTION } from '@src/features/shift-post-v2/steps/acceptance-criteria-step'
import { useAccordionsLogic } from '@src/features/shift-post-v2/steps/invite-upshifters-step/components/invite-upshifters-section/hooks/useAccordionsLogic'
import { SortableRoster } from '@src/features/shift-post-v2/steps/invite-upshifters-step/components/invite-upshifters-section/components/sortable-roster'
import { connect, FormikProps } from 'formik'
import { useDispatch, useSelector } from 'react-redux'
import { useMemo } from 'react'
import { getSelectedAcceptanceCriteriaOption } from '@src/features/shift-post-v2/utils'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { IInviteUpshiftersFormValues } from '../../invite-upshifters-section.types'
import { IconWithMargin } from './invite-upshifters-content.style'

interface IInviteUpshiftersContentProps {
  formik?: FormikProps<IInviteUpshiftersFormValues>
  isConfirmStep?: boolean
}

export const InviteUpshiftersContent = connect<IInviteUpshiftersContentProps>(
  ({ formik, isConfirmStep }) => {
    const { values } = formik
    const dispatch = useDispatch()
    const businessEntity = useSelector(selectBusinessEntity)

    const selectedAcceptanceCriteriaOption = useMemo(() => {
      return getSelectedAcceptanceCriteriaOption(values.auto_accept_by_rosters, values.auto_accept)
    }, [values.auto_accept_by_rosters, values.auto_accept])

    // Acceptance criteria selected rosters
    const acSelectedRosters = values.auto_accept_rosters

    // All rosters
    const allRosters = useSelector(selectRosters)

    const rosters =
      selectedAcceptanceCriteriaOption === ACCEPTANCE_CRITERIA_OPTION.VERIFIED_FROM_ROSTERS
        ? acSelectedRosters
        : allRosters

    const { expandedRosterIds, handleExpandRosterToggle } = useAccordionsLogic()

    // Set up sensors for drag and drop
    const sensors = useSensors(
      useSensor(PointerSensor),
      useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
      })
    )

    const handleDragEnd = (event: DragEndEvent) => {
      const { active, over } = event

      if (over && active.id !== over.id) {
        const oldIndex = rosters.findIndex((roster) => roster.id === active.id)
        const newIndex = rosters.findIndex((roster) => roster.id === over.id)

        dispatch(ReorderRosters({ oldIndex, newIndex }))
      }
    }

    const enableDragAndDrop =
      !isConfirmStep ||
      selectedAcceptanceCriteriaOption !== ACCEPTANCE_CRITERIA_OPTION.VERIFIED_FROM_ROSTERS

    return (
      <Box>
        {enableDragAndDrop && (
          <InfoBanner
            description={
              <>
                Use the drag handle <IconWithMargin icon={faGripVertical} size='lg' /> next to each
                roster to reorder them. Rosters at the top will be invited first.
              </>
            }
            icon={faInfoCircle}
          />
        )}

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis]}
        >
          <SortableContext
            items={rosters.map((roster) => roster.id)}
            strategy={verticalListSortingStrategy}
          >
            {rosters.map((roster) => {
              const isExpanded = expandedRosterIds.includes(roster.id)

              return (
                <SortableRoster
                  key={roster.id}
                  roster={roster}
                  isExpanded={isExpanded}
                  onToggle={handleExpandRosterToggle}
                  businessId={businessEntity.id}
                  enableDragAndDrop={enableDragAndDrop}
                  isUnavailable={!(roster.is_available_for_invite ?? true)}
                />
              )
            })}
          </SortableContext>
        </DndContext>
      </Box>
    )
  }
)
